import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Calendar, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const POLL_MS = 30_000; // 30s

/**
 * Header bell that polls /api/admin/stats every 30 seconds.
 * Shows a red dot + count when there are bookings awaiting payment or
 * inquiries with status === "new". Clicking opens a small panel with
 * quick links into the right admin workspace.
 */
export default function AdminNotificationBell() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [stats, setStats] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () => {
      api
        .get("/admin/stats")
        .then((r) => {
          if (!cancelled) setStats(r.data);
        })
        .catch(() => {});
    };
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const pendingBookings = stats?.bookings?.pending ?? 0;
  const newContacts = stats?.contacts?.new ?? 0;
  const total = pendingBookings + newContacts;

  const t = {
    aria: isAr ? "إشعارات" : "Notifications",
    title: isAr ? "الإشعارات الجديدة" : "New notifications",
    bookingsLabel: isAr
      ? `${pendingBookings} حجز بانتظار الدفع`
      : `${pendingBookings} booking${pendingBookings === 1 ? "" : "s"} awaiting payment`,
    contactsLabel: isAr
      ? `${newContacts} استفسار لم يُرَدّ عليه`
      : `${newContacts} unread inquir${newContacts === 1 ? "y" : "ies"}`,
    none: isAr ? "لا توجد إشعارات جديدة." : "All caught up.",
    seeAll: isAr ? "افتح لوحة التحكم" : "Open dashboard",
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="admin-notification-bell"
          aria-label={t.aria}
          className="relative inline-flex items-center justify-center w-9 h-9 hover:opacity-80 outline-none"
          style={{ color: "var(--ink-soft)" }}
        >
          <Bell className="w-4 h-4" />
          {total > 0 && (
            <span
              data-testid="admin-notification-count"
              className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-[18px] text-center"
            >
              {total > 99 ? "99+" : total}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="rounded-sm min-w-[280px] mt-2 p-0 overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
          <p className="eyebrow">{t.title}</p>
        </div>

        {total === 0 ? (
          <div className="px-4 py-6 text-sm text-slate-500 text-center">
            {t.none}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {pendingBookings > 0 && (
              <li>
                <Link
                  to="/admin/bookings"
                  data-testid="notification-bookings-link"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  <span className="w-8 h-8 rounded-sm bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4" />
                  </span>
                  <span className="text-sm leading-snug">
                    {t.bookingsLabel}
                  </span>
                </Link>
              </li>
            )}
            {newContacts > 0 && (
              <li>
                <Link
                  to="/admin/contacts"
                  data-testid="notification-contacts-link"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  <span className="w-8 h-8 rounded-sm bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4" />
                  </span>
                  <span className="text-sm leading-snug">
                    {t.contactsLabel}
                  </span>
                </Link>
              </li>
            )}
          </ul>
        )}

        <Link
          to="/admin"
          data-testid="notification-see-all"
          className="block px-4 py-3 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 dark:hover:text-amber-500 border-t border-slate-100 dark:border-slate-800 text-center"
        >
          {t.seeAll}
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
